// Mirrors backend com.kervolt.oms.dataclass.bank.* DTOs (spec §2.8) 1:1.

import { BankAccount } from './bankAccount.interface';

export type BankReconciliationStatus = 'IN_PROGRESS' | 'COMPLETED';
export type BankStatementLineSource = 'IMPORTED' | 'MANUAL';
export type MatchType = 'SUGGESTED' | 'MANUAL' | 'ADJUSTMENT';
export type MatchSuggestionReason = 'REFERENCE' | 'EXACT_DATE' | 'DATE_WINDOW' | 'CHEQUE';

// Chip color per BankReconciliationStatus, for MUI <Chip color=...>.
export const BANK_REC_STATUS_COLORS: Record<BankReconciliationStatus, 'warning' | 'success'> = {
    IN_PROGRESS: 'warning',
    COMPLETED: 'success',
};

export interface BankReconciliation {
    id: string;
    bankAccountId: string;
    institutionId: string;
    statementNumber: string;
    statementDate: string;
    statementOpeningBalance: number;
    statementClosingBalance: number;
    status: BankReconciliationStatus;
    completedById?: string;
    completedAt?: string;
}

export interface BankStatementLine {
    id: string;
    bankReconciliationId: string;
    lineDate: string;
    description?: string;
    amount: number;
    statementBalance?: number;
    source: BankStatementLineSource;
    externalRef?: string;
    matched: boolean;
    matchGroupId?: string;
    matchType?: MatchType;
    matchedJournalEntryLineIds?: string[];
}

export interface BankLedgerLine {
    journalEntryLineId: string;
    entryNumber: string;
    date: string;
    description?: string;
    entryType: 'DEBIT' | 'CREDIT';
    amount: number;
}

export interface MatchSuggestion {
    bankStatementLineId: string;
    journalEntryLineId: string;
    reason: MatchSuggestionReason;
}

export interface TieOutSummary {
    bookBalance: number;
    outstandingTotal: number;
    reconciledBalance: number;
    statementClosingBalance: number;
    difference: number;
}

export interface BankReconciliationWorkspace {
    reconciliation: BankReconciliation;
    bankAccount: BankAccount;
    statementLines: BankStatementLine[];
    unmatchedLedgerLines: BankLedgerLine[];
    suggestions: MatchSuggestion[];
    tieOut: TieOutSummary;
}

export interface CreateReconciliationDTO {
    bankAccountId: string;
    statementDate: string;
    statementOpeningBalance: number;
    statementClosingBalance: number;
    institutionId: string;
}

export interface BankStatementLineCreateDTO {
    lineDate: string;
    description?: string;
    amount: number;
    statementBalance?: number;
    externalRef?: string;
}

export interface MatchRequestDTO {
    bankStatementLineIds: string[];
    journalEntryLineIds: string[];
}

export interface UnmatchRequestDTO {
    matchGroupId: string;
}

export interface AdjustmentRequestDTO {
    bankStatementLineId: string;
    accountId: string;
    description?: string;
    segmentId?: string;
}

export interface StatementImportResult {
    lines: BankStatementLine[];
    importedCount: number;
    skippedDuplicateCount: number;
    detectedFormat: string;
}

export interface UnreconciledReport {
    bankAccountId: string;
    bankName: string;
    accountNumber: string;
    glCashAccountCode: string;
    glCashAccountName: string;
    asOf: string;
    bookBalance: number;
    depositsInTransit: BankLedgerLine[];
    depositsInTransitTotal: number;
    outstandingWithdrawals: BankLedgerLine[];
    outstandingWithdrawalsTotal: number;
    outstandingTotal: number;
    adjustedBookBalance: number;
    unmatchedStatementLines: BankStatementLine[];
}
